import { Box } from "@chakra-ui/react";
import { colors, ColorIcon, customEvent, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { ReactNode, useContext, useEffect } from "react";
import { MUBINSO_PHONE, MubinsoEntry, mubinsoEntryTrigger } from "./mubinsoConstants";
import { MubinsoScreenChromeContext } from "./mubinsoScreenChromeContext";
import { MubinsoCheckItem, MubinsoCtaButton, MubinsoScreenLayout } from "./MubinsoUI";

export interface MubinsoReview {
  text: string;
  meta: string;
}

/**
 * 전환 화면 공용 부수효과. 레이아웃이 다른 전환 화면(임종·임박 개편안 등)도
 * 이 훅만 부르면 계측과 크롬 처리가 같아진다.
 *
 * - RenewalFooter 숨김(화면 벗어나면 복원)
 * - 전환화면 도착 이벤트 1회. 퍼널이 달라도 이 지표는 공통이라 여기서 발화한다
 * - 최상단 스크롤. footer 숨김 리렌더 이후 실행되도록 지연
 */
export function useMubinsoConversionChrome(entry?: MubinsoEntry) {
  const chrome = useContext(MubinsoScreenChromeContext);

  useEffect(() => {
    chrome?.setHideFooter(true);
    return () => chrome?.setHideFooter(false);
  }, [chrome]);

  // 공통(전 퍼널 합산) + 개별(퍼널별 A/B 분모). 합산 지표에는 공통만 쓸 것
  useEffect(() => {
    customEvent({ newGtm: { action: "mubinso_request_complete" } });
    // entry 가 없는 경로는 공통만 발화한다
    if (!entry) return;
    customEvent({
      newGtm: {
        path: "mubinso_funnel",
        trigger_and_target: mubinsoEntryTrigger(entry, "conv_view"),
      },
    });
  }, [entry]);

  useEffect(() => {
    const t = setTimeout(() => window.scrollTo(0, 0), 0);
    return () => clearTimeout(t);
  }, []);
}

/**
 * 퍼널 마지막 전환 화면(공용). 체크리스트 + 후기 + (옵션)전화 링크 + CTA 2개(자료/상담).
 * 각 CTA는 funnel이 소유한 리드 모달을 연다.
 */
export function MubinsoConversionScreen({
  headline,
  sub,
  checkItems,
  checkCard,
  checkSpacing = 12,
  recap,
  reviewTitle,
  reviews = [],
  showPhoneLink = false,
  onPhoneClick,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  secondaryVariant = "button",
  secondaryHint,
  entry,
  onPrev,
}: {
  /** 지정하면 화면 위에 「‹ 이전」을 띄운다(E4) */
  onPrev?: () => void;
  headline: ReactNode;
  sub?: ReactNode;
  checkItems: { text: ReactNode; underline?: boolean }[];
  /** 지정하면 체크 항목 대신 이 카드를 둔다(E4 결과 화면의 「받아보실 자료」 미리보기) */
  checkCard?: ReactNode;
  /** 체크 항목 사이 간격(기본 12) */
  checkSpacing?: number;
  /**
   * 체크 항목과 후기 사이에 끼울 되짚기 블록. 고객이 방금 고른 조건을 다시 보여주는 자리다.
   * 체크 항목은 "무엇을 받는지"를, 이 블록은 "무엇에 대해 받는지"를 말한다.
   */
  recap?: ReactNode;
  /** 비우면(reviews 없음) 후기 섹션을 그리지 않는다(E4 결과) */
  reviewTitle?: string;
  reviews?: MubinsoReview[];
  showPhoneLink?: boolean;
  onPhoneClick?: () => void;
  primaryLabel: string;
  onPrimary: () => void;
  /** 비우면 보조 CTA 를 그리지 않는다(E4 비용 퍼널 결과는 버튼이 하나다) */
  secondaryLabel?: string;
  onSecondary?: () => void;
  /**
   * 보조 CTA 모양. button=기존 아웃라인 버튼(두 CTA가 대등할 때).
   * link=밑줄 텍스트 링크(주 CTA에 무게를 몰아주고 보조는 빠져나갈 길만 남길 때).
   */
  secondaryVariant?: "button" | "link";
  /** link 변형에서 링크 위에 붙는 안내 한 줄("아직 천천히 알아보는 중이라면"). */
  secondaryHint?: string;
  /** 진입 구분. 퍼널별 개별 이벤트 발화에 쓴다. 없으면 공통 이벤트만 */
  entry?: MubinsoEntry;
}) {
  useMubinsoConversionChrome(entry);

  return (
    <MubinsoScreenLayout
      onPrev={onPrev}
      footer={
        <>
          {showPhoneLink && (
            <Box
              cursor="pointer"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="8px"
              pb="2px"
              onClick={onPhoneClick}
            >
              <ColorIcon name="icon-call-mono" width={18} height={18} color={colors.orange650} />
              <Box className="body_14_m gray600">
                급하시다면{" "}
                <span
                  style={{ color: colors.orange650, fontWeight: 700, textDecoration: "underline" }}
                >
                  {MUBINSO_PHONE} 바로 전화하기
                </span>
              </Box>
            </Box>
          )}
          <MubinsoCtaButton label={primaryLabel} onClick={onPrimary} />
          {!secondaryLabel ? null : secondaryVariant === "button" ? (
            <MubinsoCtaButton
              label={secondaryLabel}
              variant="secondary"
              onClick={() => onSecondary?.()}
            />
          ) : (
            <BaseStack width="100%" spacing={4} alignItems="center" pt={4}>
              {secondaryHint && <Box className="body_14_r gray400">{secondaryHint}</Box>}
              <Box
                as="span"
                cursor="pointer"
                className="body_14_m gray600"
                style={{ textDecoration: "underline", textUnderlineOffset: "3px" }}
                onClick={onSecondary}
              >
                {secondaryLabel}
              </Box>
            </BaseStack>
          )}
        </>
      }
    >
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        {headline}
      </Box>
      {sub && (
        <>
          <Spacing gap={12} />
          <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
            {sub}
          </Box>
        </>
      )}

      <Spacing gap={24} />
      {checkCard ?? (
        <BaseStack width="100%" spacing={checkSpacing}>
          {checkItems.map((item, i) => (
            <MubinsoCheckItem key={i} underline={item.underline} alignStart>
              {item.text}
            </MubinsoCheckItem>
          ))}
        </BaseStack>
      )}

      {recap && (
        <>
          <Spacing gap={20} />
          {recap}
        </>
      )}

      {reviews.length > 0 && (
        <>
          <Spacing gap={28} />
          <BaseStack width="100%" bg={colors.warmGray100} borderRadius="16px" p={20} spacing={14}>
            {reviewTitle && <Box className="subtitle_14_b gray900">{reviewTitle}</Box>}
            <BaseStack width="100%" spacing={10}>
              {reviews.map((review, i) => (
                <BaseStack
                  key={i}
                  width="100%"
                  bg={colors.white}
                  borderRadius="12px"
                  p={16}
                  spacing={8}
                >
                  <Box className="body_14_r gray800" whiteSpace="pre-line">
                    {review.text}
                  </Box>
                  <Box className="caption_12_b" color={colors.gray400}>
                    {review.meta}
                  </Box>
                </BaseStack>
              ))}
            </BaseStack>
          </BaseStack>
        </>
      )}
    </MubinsoScreenLayout>
  );
}
